import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import Candidate from './candidate';
import VirtualizedList from '../virtualizedList';
import { formatDate, formatTimeAgo } from '../../../utility/dateUtils';
import { asyncDebounce } from '../../../utility/helpers';

const KEYBOARD_SELECT_DELAY = 250;

const useStyles = makeStyles(({ spacing, breakpoints }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    marginTop: spacing(6),
    minHeight: 0,
    outline: 'none',
    width: '100%',
  },
  scrollY: {
    right: -11,
    [breakpoints.up('xl')]: {
      right: -18,
    },
  },
  label: {
    fontSize: 10,
    lineHeight: '14px',
    [breakpoints.up('xl')]: {
      fontSize: 14,
      lineHeight: '18px',
    },
  },
  date: {
    marginLeft: spacing(0.75),
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    minWidth: 0,
  },
  metaSection: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
}));

const CandidatesList = ({
  translations,
  candidateClass = undefined,
  users = [],
  selectedCandidateId = null,
  hasLabel = false,
  onSelect,
}) => {
  const classes = useStyles();
  const rootRef = useRef(null);
  const virtualizerRef = useRef(null);
  const highlightedIdRef = useRef(selectedCandidateId);
  const pendingKeyboardIdRef = useRef(null);
  const skipKeyboardSelectRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  const [highlightedId, setHighlightedId] = useState(selectedCandidateId);

  const debouncedSelectRef = useRef(null);

  if (!debouncedSelectRef.current) {
    debouncedSelectRef.current = asyncDebounce(candidate => {
      if (skipKeyboardSelectRef.current) return;

      onSelectRef.current(candidate);
    }, KEYBOARD_SELECT_DELAY);
  }

  onSelectRef.current = onSelect;

  const getVirtualCandidateSize = useCallback(() => 66, []);

  const updateHighlightedId = useCallback(candidateId => {
    highlightedIdRef.current = candidateId;
    setHighlightedId(candidateId);
  }, []);

  useEffect(() => {
    if (
      pendingKeyboardIdRef.current == null ||
      pendingKeyboardIdRef.current === selectedCandidateId
    ) {
      pendingKeyboardIdRef.current = null;
      updateHighlightedId(selectedCandidateId);
    }
  }, [selectedCandidateId, updateHighlightedId]);

  useEffect(
    () => () => {
      skipKeyboardSelectRef.current = true;
    },
    []
  );

  const focusList = useCallback(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    if (selectedCandidateId == null) return;

    const { activeElement } = document;
    const tagName = activeElement?.tagName;

    if (
      tagName === 'INPUT' ||
      tagName === 'TEXTAREA' ||
      activeElement?.isContentEditable
    ) {
      return;
    }

    focusList();
  }, [focusList, selectedCandidateId]);

  const handleSelect = useCallback(
    candidate => {
      skipKeyboardSelectRef.current = true;
      pendingKeyboardIdRef.current = null;
      updateHighlightedId(candidate.id);
      onSelect(candidate);
      focusList();
    },
    [focusList, onSelect, updateHighlightedId]
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const { target } = event;
      const tagName = target?.tagName;

      if (
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        target?.isContentEditable
      ) {
        return;
      }

      event.preventDefault();

      if (!users?.length) return;

      const currentIndex = users.findIndex(
        user => user.id === highlightedIdRef.current
      );
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      let nextIndex = currentIndex + direction;

      if (currentIndex === -1) {
        nextIndex = direction === 1 ? 0 : users.length - 1;
      }

      if (nextIndex < 0 || nextIndex >= users.length) return;

      const nextCandidate = users[nextIndex];

      skipKeyboardSelectRef.current = false;
      pendingKeyboardIdRef.current = nextCandidate.id;
      updateHighlightedId(nextCandidate.id);
      virtualizerRef.current?.scrollToIndex(nextIndex, { align: 'auto' });
      debouncedSelectRef.current(nextCandidate);
    },
    [updateHighlightedId, users]
  );

  const renderLabel = useCallback(
    user => {
      return (
        <span className={classes.meta}>
          <span className={classes.metaSection}>
            <Typography
              className={classes.label}
              variant="body2"
              component="span"
            >
              {user?.self_applied ? translations.applied : translations.added}:
            </Typography>
            <Typography
              className={classNames(classes.label, classes.date)}
              variant="body2"
              component="span"
            >
              {user?.added_to_job_at ? formatDate(user.added_to_job_at) : '-'}
            </Typography>
          </span>
          <span className={classes.metaSection}>
            <Typography
              className={classes.label}
              variant="body2"
              component="span"
            >
              {translations.updated}:
            </Typography>
            <Typography
              className={classNames(classes.label, classes.date)}
              variant="body2"
              component="span"
            >
              {user?.last_update
                ? formatTimeAgo(user.last_update, translations)
                : '-'}
            </Typography>
          </span>
        </span>
      );
    },
    [
      classes.date,
      classes.label,
      classes.meta,
      classes.metaSection,
      translations,
    ]
  );

  const renderCandidate = useCallback(
    user => (
      <Candidate
        className={candidateClass}
        user={user}
        label={renderLabel(user)}
        hasLabel={hasLabel}
        isSelected={highlightedId === user.id}
        onSelectCandidate={handleSelect}
      />
    ),
    [candidateClass, handleSelect, hasLabel, highlightedId, renderLabel]
  );

  return (
    <div
      ref={rootRef}
      className={classes.root}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <VirtualizedList
        verticalScrollClass={classes.scrollY}
        items={users}
        count={users?.length ?? 0}
        gap={6}
        estimateSize={getVirtualCandidateSize}
        renderItem={renderCandidate}
        virtualizerRef={virtualizerRef}
      />
    </div>
  );
};

CandidatesList.propTypes = {
  candidateClass: PropTypes.string,
  translations: PropTypes.object.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({})),
  selectedCandidateId: PropTypes.number,
  hasLabel: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

export default CandidatesList;
